import React from "react";
import { Link, useLocation } from "react-router-dom";
import "./Stores.css";
import StoreCommissionDialog from "./StoreCommissionDialog";
import StoreLogoUpload from "./StoreLogoUpload";
import useFetch from "../../hooks/useFetch";
import ShareStoreDialog from "../ProductGroups/ShareStoreDialog";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import usePagination from "../../hooks/usePagination";
import GeneratePaymentBatch, { StoreBatchFilters } from "./GeneratePaymentBatch";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { hasStoreBalance, formatDbTimestamp, getViewersTimeZone } from "../../utils";

const StoreToggleBtn: React.FC<{
  storeId: number;
  isActive: boolean;
  onDone: () => void;
  onError: (content: React.ReactNode) => void;
}> = ({ storeId, isActive, onDone, onError }) => {
  const { fetchData: toggleStatus } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/stores/togglestatus/${storeId}`,
    method: "PUT",
  });

  const handleClick = async () => {
    const action = isActive ? "deactivate" : "activate";
    const confirmed = window.confirm(`Are you sure you want to ${action} this store?`);
    if (!confirmed) return;
    const result = await toggleStatus({ status: isActive ? 0 : 1 });
    if (result?.success) {
      onDone();
    } else {
      const isAuthError = result?.code === 401 || result?.message?.toLowerCase().includes("token");
      const msg = isAuthError
        ? <>⚠️ Session expired. Please <a href="/login" className="login-error-showup">login</a> again.</>
        : (result?.message?.replace(/^Error in \w+:\s*/i, "") || `Failed to ${action} store.`);
      if (!isAuthError) window.alert(msg);
      if (isAuthError) {
        onError(msg);
      }
    }
  };

  return (
    <span
      id={`toggle-btn-${storeId}`}
      className="stores-hidden-toggle-btn"
      onClick={handleClick}
    >
      {isActive ? "Deactivate" : "Activate"}
    </span>
  );
};

// Date helpers
function safeFormatDate(val?: string | Date | null): string {
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  if (!val) return "";
  try {
    if (typeof val === "string" && val.includes("-")) {
      const parts = val.split("T")[0].split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const monthIdx = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        if (monthIdx >= 0 && monthIdx < 12) {
          return `${MONTHS[monthIdx]} ${day}, ${year}`;
        }
      }
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}


function usToIso(v: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((v || "").trim());
  if (!m) return null;
  const year = +m[3];
  const month = +m[1];
  const day = +m[2];
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const formatDateToYMD = (date: Date | string | null | undefined): string | null => {
  if (!date) return null;
  if (typeof date === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    return usToIso(date);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (d: string | Date | null | undefined): string => {
  if (!d) return "—";
  // Formatted in the US business timezone; see utils.ts for rationale
  return formatDbTimestamp(d, { month: "short", day: "numeric", year: "numeric" });
};

function money(n: any): string {
  const val = typeof n === "number" ? n : parseFloat(n || "0");
  if (isNaN(val)) return "$0.00";
  return (val < 0 ? "-$" : "$") + Math.abs(val).toFixed(2);
}

function blankIfZero(n: any): string {
  if (n == null || n === "" || n === 0 || n === "0" || n === "0.00") return "";
  const val = typeof n === "number" ? n : parseFloat(n);
  if (isNaN(val) || Math.abs(val) < 0.001) return "";
  return money(val);
}

const Stores: React.FC = () => {
  const [fetchUrl, setFetchUrl] = React.useState(`${import.meta.env.VITE_API_URL}/stores`);

  const {
    data: stores,
    setData: setStores,
    error,
    errorContent,
    hasMore,
    isLoading,
    isLoadingMore,
    hasFetched,
    fetchInitial,
    fetchMore,
    responseMeta,
  } = usePagination({ url: fetchUrl, method: "GET", listKey: "stores" });

  const [toggleError, setToggleError] = React.useState<React.ReactNode>(null);
  const [shareDialog, setShareDialog] = React.useState<{ open: boolean; storeId: number; storeName: string; }>({
    open: false,
    storeId: 0,
    storeName: "",
  });

  const [menuAnchor, setMenuAnchor] = React.useState<{ el: HTMLElement; storeId: number } | null>(null);

  const location = useLocation();

  // Selection states
  const [selectedStoreIds, setSelectedStoreIds] = React.useState<number[]>([]);
  const [isSelectAllActive, setIsSelectAllActive] = React.useState<boolean>(false);
  const [excludedStoreIds, setExcludedStoreIds] = React.useState<number[]>([]);
  const lastClickedIndexRef = React.useRef<number | null>(null);
  const lastActionSelectedRef = React.useRef<boolean | null>(null);
  const isDraggingRef = React.useRef(false);
  const dragStartIndexRef = React.useRef<number | null>(null);
  const dragTargetSelectedRef = React.useRef<boolean>(true);
  const didDragMoveRef = React.useRef<boolean>(false);

  // Filters state
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc" | null>(null);
  const [payoutSortOrder, setPayoutSortOrder] = React.useState<"asc" | "desc" | null>(null);

  // Mockup Filter card states
  const [unpaidOnly, setUnpaidOnly] = React.useState<boolean>(false);
  const [dateFrom, setDateFrom] = React.useState<Date | null>(null);
  const [dateTo, setDateTo] = React.useState<Date | null>(null);
  const [minBalance, setMinBalance] = React.useState<string>("");
  const [filterError, setFilterError] = React.useState<string>("");

  // Applied filter state
  const [appliedUnpaidOnly, setAppliedUnpaidOnly] = React.useState<boolean>(false);
  const [appliedDateFrom, setAppliedDateFrom] = React.useState<Date | null>(null);
  const [appliedDateTo, setAppliedDateTo] = React.useState<Date | null>(null);
  const [appliedMinBalance, setAppliedMinBalance] = React.useState<string>("");

  const selectableStores = React.useMemo(() => (stores || []).filter(hasStoreBalance), [stores]);
  const selectableStoreIds = React.useMemo(() => new Set(selectableStores.map((s: any) => s.id)), [selectableStores]);

  const isStoreSelected = React.useCallback(
    (storeId: number) => {
      if (!selectableStoreIds.has(storeId)) {
        return false;
      }
      if (isSelectAllActive) {
        return !excludedStoreIds.includes(storeId);
      }
      return selectedStoreIds.includes(storeId);
    },
    [isSelectAllActive, excludedStoreIds, selectedStoreIds, selectableStoreIds]
  );

  const resetSelection = () => {
    setSelectedStoreIds([]);
    setExcludedStoreIds([]);
    setIsSelectAllActive(false);
    lastClickedIndexRef.current = null;
  };

  // Global pointerup/touchend listener to finish slide selection
  React.useEffect(() => {
    const handleGlobalPointerUp = () => {
      isDraggingRef.current = false;
      dragStartIndexRef.current = null;
      setTimeout(() => {
        didDragMoveRef.current = false;
      }, 50);
    };
    window.addEventListener("pointerup", handleGlobalPointerUp);
    window.addEventListener("touchend", handleGlobalPointerUp);
    return () => {
      window.removeEventListener("pointerup", handleGlobalPointerUp);
      window.removeEventListener("touchend", handleGlobalPointerUp);
    };
  }, []);

  const handleToggleStore = (storeId: number) => {
    if (isSelectAllActive) {
      setExcludedStoreIds((prev) => {
        const nextExcluded = prev.includes(storeId)
          ? prev.filter((id) => id !== storeId)
          : [...prev, storeId];

        const allSelectableIds = stores.filter(hasStoreBalance).map((s: any) => s.id);
        const hasRemaining = allSelectableIds.some((id) => !nextExcluded.includes(id));
        if (!hasRemaining) {
          setIsSelectAllActive(false);
          setSelectedStoreIds([]);
          return [];
        }
        return nextExcluded;
      });
    } else {
      setSelectedStoreIds((prev) =>
        prev.includes(storeId)
          ? prev.filter((id) => id !== storeId)
          : [...prev, storeId]
      );
    }
  };

  const handleRangeSelect = (fromIdx: number, toIdx: number, targetState?: boolean) => {
    const start = Math.min(fromIdx, toIdx);
    const end = Math.max(fromIdx, toIdx);
    const rangeIds = stores
      .slice(start, end + 1)
      .filter(hasStoreBalance)
      .map((s: any) => s.id);
    const shouldSelect = targetState !== undefined ? targetState : true;

    if (isSelectAllActive) {
      setExcludedStoreIds((prev) => {
        let nextExcluded: number[];
        if (shouldSelect) {
          const removeSet = new Set(rangeIds);
          nextExcluded = prev.filter((id) => !removeSet.has(id));
        } else {
          nextExcluded = Array.from(new Set([...prev, ...rangeIds]));
        }

        const allSelectableIds = stores.filter(hasStoreBalance).map((s: any) => s.id);
        const hasRemaining = allSelectableIds.some((id) => !nextExcluded.includes(id));
        if (!hasRemaining) {
          setIsSelectAllActive(false);
          setSelectedStoreIds([]);
          return [];
        }
        return nextExcluded;
      });
    } else {
      setSelectedStoreIds((prev) => {
        if (shouldSelect) {
          return Array.from(new Set([...prev, ...rangeIds]));
        } else {
          const removeSet = new Set(rangeIds);
          return prev.filter((id) => !removeSet.has(id));
        }
      });
    }
  };

  const handleStoreSelect = (e: React.MouseEvent, storeId: number, index: number) => {
    if (didDragMoveRef.current) {
      return;
    }

    if (e.shiftKey && lastClickedIndexRef.current !== null) {
      const start = Math.min(lastClickedIndexRef.current, index);
      const end = Math.max(lastClickedIndexRef.current, index);
      const rangeStores = stores.slice(start, end + 1).filter(hasStoreBalance);
      const rangeIds = rangeStores.map((s: any) => s.id);

      if (rangeIds.length === 0) {
        return;
      }

      const isRangeAllSelected = rangeIds.every((id: number) => isStoreSelected(id));
      const targetState = !isRangeAllSelected;

      handleRangeSelect(lastClickedIndexRef.current, index, targetState);
      lastActionSelectedRef.current = targetState;
      lastClickedIndexRef.current = index;
    } else {
      const store = stores[index];
      if (!hasStoreBalance(store)) {
        return;
      }
      const isCurrentSelected = isStoreSelected(storeId);
      const nextState = !isCurrentSelected;
      handleToggleStore(storeId);
      lastActionSelectedRef.current = nextState;
      lastClickedIndexRef.current = index;
    }
  };

  const handleRowClick = (e: React.MouseEvent, storeId: number, index: number) => {
    const store = stores[index];
    if (!e.shiftKey && !hasStoreBalance(store)) {
      return;
    }
    const target = e.target as HTMLElement;
    if (
      target.closest(
        "a, button, input, .kebab, .store-logo-upload, .slu-trigger, .MuiMenu-root, .MuiMenuItem-root, .MuiBackdrop-root, .stores-action-menu-paper, [role='menu'], [role='menuitem']"
      )
    ) {
      return;
    }
    handleStoreSelect(e, storeId, index);
  };

  const handleCheckboxPointerDown = (e: React.PointerEvent, storeId: number, index: number) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (e.shiftKey) return;
    const store = stores[index];
    if (!hasStoreBalance(store)) return;

    isDraggingRef.current = true;
    didDragMoveRef.current = false;
    dragStartIndexRef.current = index;
    const willBeSelected = !isStoreSelected(storeId);
    dragTargetSelectedRef.current = willBeSelected;
  };

  const handleRowPointerEnter = (index: number) => {
    if (!isDraggingRef.current || dragStartIndexRef.current === null) return;
    didDragMoveRef.current = true;
    handleRangeSelect(dragStartIndexRef.current, index, dragTargetSelectedRef.current);
    lastClickedIndexRef.current = index;
    lastActionSelectedRef.current = dragTargetSelectedRef.current;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current || dragStartIndexRef.current === null) return;
    const touch = e.touches[0];
    if (!touch) return;
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const rowEl = el?.closest("tr[data-index]") as HTMLElement;
    if (rowEl && rowEl.dataset.index) {
      const idx = parseInt(rowEl.dataset.index, 10);
      if (!isNaN(idx)) {
        handleRowPointerEnter(idx);
      }
    }
  };

  // When Select All is active, automatically select newly fetched stores
  React.useEffect(() => {
    if (isSelectAllActive && stores && stores.length > 0) {
      const allSelectableIds = stores.filter(hasStoreBalance).map((s: any) => s.id);
      const remainingSelected = allSelectableIds.filter((id) => !excludedStoreIds.includes(id));
      setSelectedStoreIds(remainingSelected);

      if (allSelectableIds.length > 0 && remainingSelected.length === 0) {
        resetSelection();
      }
    }
  }, [stores, isSelectAllActive, excludedStoreIds]);

  const isAllLoadedSelected = selectableStores.length > 0 && selectableStores.every((s: any) => selectedStoreIds.includes(s.id));
  const isAllSelected = isSelectAllActive
    ? excludedStoreIds.length === 0
    : isAllLoadedSelected;
  const isSomeSelected = !isAllSelected && (
    isSelectAllActive
      ? (excludedStoreIds.length > 0 && selectedStoreIds.length > 0)
      : selectedStoreIds.length > 0
  );

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      resetSelection();
      lastActionSelectedRef.current = false;
      lastClickedIndexRef.current = null;
    } else {
      if (!stores || stores.length === 0) return;
      const allSelectableIds = selectableStores.map((s: any) => s.id);
      setIsSelectAllActive(true);
      setExcludedStoreIds([]);
      setSelectedStoreIds(allSelectableIds);
      lastActionSelectedRef.current = true;
      lastClickedIndexRef.current = null;
    }
  };

  // Disable automatic browser scroll restoration
  React.useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);
  const [displayedDataUrl, setDisplayedDataUrl] = React.useState<string | null>(null);

  // Fetch initial on mount/nav/url changes
  React.useEffect(() => {
    setDisplayedDataUrl(null);
    fetchInitial().then(() => {
      setDisplayedDataUrl(fetchUrl);
    });
    window.scrollTo(0, 0);
  }, [location, fetchUrl, fetchInitial]);

  // Infinite scroll
  const hasMoreRef = React.useRef(hasMore);
  React.useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const isLoadingMoreRef = React.useRef(isLoadingMore);
  React.useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const isFetchingRef = React.useRef(false);
  const isIntersectingRef = React.useRef(false);
  const cooldownTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, []);

  const fetchMoreStores = React.useCallback(async () => {
    if (!hasMoreRef.current || isLoadingMoreRef.current || isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      await fetchMore();
    } finally {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      cooldownTimerRef.current = setTimeout(() => {
        isFetchingRef.current = false;
        if (isIntersectingRef.current && hasMoreRef.current && !isLoadingMoreRef.current) {
          fetchMoreStores();
        }
      }, 400);
    }
  }, [fetchMore]);

  const observerTargetRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!hasMore || !hasFetched || isLoading || stores.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries[0]?.isIntersecting ?? false;
        isIntersectingRef.current = isIntersecting;

        if (isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current && !isFetchingRef.current && !isLoading) {
          fetchMoreStores();
        }
      },
      { rootMargin: "100px", threshold: 0.1 }
    );

    const currentTarget = observerTargetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, hasFetched, isLoading, stores.length, fetchMoreStores]);

  // URL builder
  const buildStoresUrl = (
    unpaidOnlyVal: boolean = appliedUnpaidOnly,
    fromVal: Date | string | null = appliedDateFrom,
    toVal: Date | string | null = appliedDateTo,
    minBalVal: string = appliedMinBalance,
    sortVal: "asc" | "desc" | null = sortOrder,
    searchVal: string = searchTerm,
    payoutSortVal: "asc" | "desc" | null = payoutSortOrder
  ) => {
    const params = new URLSearchParams();
    const term = searchVal.trim();
    if (term) {
      params.append("search", term);
    }

    if (unpaidOnlyVal) {
      if (minBalVal.trim()) {
        params.append("balance", minBalVal.trim());
      } else {
        params.append("balance", "0.01");
      }
      const isoFrom = formatDateToYMD(fromVal);
      if (isoFrom) {
        params.append("startDate", isoFrom);
      }
      const isoTo = formatDateToYMD(toVal);
      if (isoTo) {
        params.append("endDate", isoTo);
      }
      params.append("timezone", getViewersTimeZone());
    } else if (minBalVal.trim()) {
      params.append("balance", minBalVal.trim());
    }

    if (payoutSortVal) {
      params.append("sortBy", "last_payout");
      params.append("sort", payoutSortVal);
    } else if (sortVal) {
      params.append("sort", sortVal);
    }

    const qs = params.toString();
    return `${import.meta.env.VITE_API_URL}/stores${qs ? `?${qs}` : ""}`;
  };

  const handleSearchSubmit = (term: string) => {
    resetSelection();
    const clean = term.trim();
    setSearchTerm(clean);
    setFetchUrl(buildStoresUrl(appliedUnpaidOnly, appliedDateFrom, appliedDateTo, appliedMinBalance, sortOrder, clean, payoutSortOrder));
  };

  const handleSearchClear = () => {
    resetSelection();
    setSearchTerm("");
    setFetchUrl(buildStoresUrl(appliedUnpaidOnly, appliedDateFrom, appliedDateTo, appliedMinBalance, sortOrder, "", payoutSortOrder));
  };

  const handleApplyFilters = () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setFilterError("From date must be before To date.");
      return;
    }
    if (minBalance.trim() && parseFloat(minBalance) < 0) {
      setFilterError("Minimum balance can't be negative.");
      return;
    }

    setFilterError("");
    setAppliedUnpaidOnly(unpaidOnly);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setAppliedMinBalance(minBalance.trim());
    resetSelection();
    setFetchUrl(buildStoresUrl(unpaidOnly, dateFrom, dateTo, minBalance.trim(), sortOrder, searchTerm, payoutSortOrder));
  };

  const handleResetFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setMinBalance("");
    setFilterError("");
    setAppliedDateFrom(null);
    setAppliedDateTo(null);
    setAppliedMinBalance("");
    resetSelection();
    setFetchUrl(buildStoresUrl(unpaidOnly, null, null, "", sortOrder, searchTerm, payoutSortOrder));
  };

  const handleUnpaidToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setUnpaidOnly(checked);
    if (checked) {
      // Immediately apply the "unpaid balance only" filter
      setAppliedUnpaidOnly(true);
      resetSelection();
      setFetchUrl(buildStoresUrl(true, dateFrom, dateTo, minBalance.trim(), sortOrder, searchTerm, payoutSortOrder));
    } else {
      setDateFrom(null);
      setDateTo(null);
      setMinBalance("");
      setFilterError("");
      setAppliedUnpaidOnly(false);
      setAppliedDateFrom(null);
      setAppliedDateTo(null);
      setAppliedMinBalance("");
      resetSelection();
      setFetchUrl(buildStoresUrl(false, null, null, "", sortOrder, searchTerm, payoutSortOrder));
    }
  };

  const handleBalanceSortToggle = () => {
    resetSelection();
    setPayoutSortOrder(null);
    const nextSort: "asc" | "desc" = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(nextSort);
    setFetchUrl(buildStoresUrl(appliedUnpaidOnly, appliedDateFrom, appliedDateTo, appliedMinBalance, nextSort, searchTerm, null));
  };

  const handlePayoutSortToggle = () => {
    resetSelection();
    setSortOrder(null);
    const nextSort: "asc" | "desc" = payoutSortOrder === "desc" ? "asc" : "desc";
    setPayoutSortOrder(nextSort);
    setFetchUrl(buildStoresUrl(appliedUnpaidOnly, appliedDateFrom, appliedDateTo, appliedMinBalance, null, searchTerm, nextSort));
  };

  const [commissionDialog, setCommissionDialog] = React.useState<{
    open: boolean;
    storeId: number;
    currentRate: number | null;
  }>({ open: false, storeId: 0, currentRate: null });

  const updateStoreInStorage = (storeId: number, updates: Record<string, any>) => {
    setStores((prev: any[]) =>
      prev.map((s) => (s.id === storeId ? { ...s, ...updates } : s))
    );
  };

  const toTitleCase = (str?: string) =>
    str ? str.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ") : "";

  const storeUrlBase = `${import.meta.env.VITE_STORE_BASE_URL}`;

  // Check if range is active
  const rangeOn = appliedUnpaidOnly && !!(appliedDateFrom || appliedDateTo);

  const filteredTotal = responseMeta?.filtered_total ?? responseMeta?.pagination?.filtered_total;
  const selectedCount = isSelectAllActive
    ? (filteredTotal != null &&
       (appliedUnpaidOnly || (appliedMinBalance.trim() && !isNaN(parseFloat(appliedMinBalance)))))
      ? Math.max(filteredTotal - excludedStoreIds.length, 0)
      : selectableStores.length - excludedStoreIds.length
    : selectedStoreIds.length;

  const activeBatchFilters: StoreBatchFilters = React.useMemo(() => {
    const f: StoreBatchFilters = {};
    if (searchTerm.trim()) f.search = searchTerm.trim();
    if (appliedUnpaidOnly) {
      if (appliedMinBalance.trim() && !isNaN(parseFloat(appliedMinBalance))) {
        f.balance = parseFloat(appliedMinBalance);
      } else {
        f.balance = 0.01;
      }
    } else if (appliedMinBalance.trim() && !isNaN(parseFloat(appliedMinBalance))) {
      f.balance = parseFloat(appliedMinBalance);
    }
    const isoStart = formatDateToYMD(appliedDateFrom);
    const isoEnd = formatDateToYMD(appliedDateTo);
    if (isoStart) f.startDate = isoStart;
    if (isoEnd) f.endDate = isoEnd;
    return f;
  }, [searchTerm, appliedUnpaidOnly, appliedMinBalance, appliedDateFrom, appliedDateTo]);

  const hasActiveBatchFilters = Boolean(
    searchTerm.trim() || appliedUnpaidOnly || appliedMinBalance.trim() || appliedDateFrom || appliedDateTo
  );

  if (error?.code === 401) {
    return (
      <div className="account-section w-clearfix stores-section">
        <div className="div-block-28">
          <div className="stores-table-wrapper">
            <div className="empty">{errorContent}</div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="account-section w-clearfix stores-section">
      <div className="div-block-28">
        <div className="stores-table-wrapper" onTouchMove={handleTouchMove}>

        {/* ---------- Page header ---------- */}
        <div className="page-head">
          <h1>Stores</h1>
          <div className="search">
            <svg
              className="search-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              type="search"
              id="searchStores"
              placeholder="Search store"
              aria-label="Search stores"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit(searchTerm);
                }
              }}
            />
            {searchTerm && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={handleSearchClear}
                aria-label="Clear search"
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </button>
            )}
          </div>
          <div className="page-head-actions">
            <GeneratePaymentBatch
              selectedStoreIds={selectedStoreIds}
              isAllSelected={isSelectAllActive}
              excludedStoreIds={excludedStoreIds}
              filters={activeBatchFilters}
              hasActiveFilters={hasActiveBatchFilters}
              onSuccess={() => {
                resetSelection();
              }}
            />
            <Link to="/add-store" className="stores-add-btn">
              + Add Store
            </Link>
          </div>
        </div>

        {/* ---------- Filter Card (inline, wraps on narrow screens) ---------- */}
        <div className="card filter">
          <label className="filter__toggle">
            <input
              type="checkbox"
              id="unpaidOnly"
              checked={unpaidOnly}
              onChange={handleUnpaidToggle}
            />
            Stores with unpaid balance
          </label>
          <div className="filter__fields" id="filterFields" hidden={!unpaidOnly}>
            <div className="stores-date-picker-wrapper">
              <DatePicker
                selected={dateFrom}
                onChange={(date: Date | null) => {
                  setFilterError("");
                  setDateFrom(date);
                }}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                maxDate={dateTo && dateTo < new Date() ? dateTo : new Date()}
                dateFormat="MMM d, yyyy"
                placeholderText="From"
                className="stores-date-input"
                popperClassName="generate-batch-datepicker-popper"
                popperPlacement="bottom-start"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyFilters();
                  }
                }}
              />
              <CalendarTodayOutlinedIcon className="stores-calendar-icon" />
            </div>
            <div className="stores-date-picker-wrapper">
              <DatePicker
                selected={dateTo}
                onChange={(date: Date | null) => {
                  setFilterError("");
                  setDateTo(date);
                }}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                minDate={dateFrom || undefined}
                maxDate={new Date()}
                dateFormat="MMM d, yyyy"
                placeholderText="To"
                className="stores-date-input"
                popperClassName="generate-batch-datepicker-popper"
                popperPlacement="bottom-start"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyFilters();
                  }
                }}
              />
              <CalendarTodayOutlinedIcon className="stores-calendar-icon" />
            </div>
            <div className={`fl fl--money ${minBalance ? "is-filled" : ""}`}>
              <input
                type="number"
                id="minBalance"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={minBalance}
                onChange={(e) => {
                  setFilterError("");
                  setMinBalance(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") {
                    e.preventDefault();
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyFilters();
                  }
                }}
              />
              <label htmlFor="minBalance">Min balance</label>
              <span className="cur" aria-hidden="true">$</span>
            </div>
            <div className="filter__actions">
              <button
                type="button"
                className="btn btn-dark"
                id="applyFilters"
                onClick={handleApplyFilters}
              >
                Apply filters
              </button>
              <button
                type="button"
                className="link"
                id="resetFilters"
                onClick={handleResetFilters}
              >
                Reset filters
              </button>
            </div>
            <p className="filter__error" id="filterError" role="alert" hidden={!filterError}>
              {filterError}
            </p>
          </div>
        </div>

        {/* ---------- Summary line ---------- */}
        <div className="summary">
          <span id="summaryText">
            {!appliedUnpaidOnly ? (
              isLoading || !hasFetched || displayedDataUrl !== fetchUrl ? (
                <>
                  {"Loading stores..."}
                </>
              ) : (
                (() => {
                  const filtered =
                    responseMeta?.filtered_total ??
                    responseMeta?.pagination?.filtered_total ??
                    stores?.length ??
                    0;
                  const total =
                    responseMeta?.total_stores ??
                    responseMeta?.pagination?.total_stores ??
                    filtered;
                  const urlSearch =
                    new URLSearchParams(fetchUrl.split("?")[1] || "").get("search") || "";
                  const hasSearch = Boolean(urlSearch.trim());
                  const hasMinBalance = Boolean(appliedMinBalance.trim());
                  return hasSearch || hasMinBalance ? (
                    <>
                      Showing{" "}
                      <strong>
                        {filtered} of {total} {total === 1 ? "store" : "stores"}
                      </strong>
                      {hasSearch ? (
                        <> matching &quot;{urlSearch}&quot;</>
                      ) : (
                        <> where balance is at least ${parseFloat(appliedMinBalance).toFixed(2)}</>
                      )}
                    </>
                  ) : (
                    <>
                      Showing{" "}
                      <strong>
                        all {total} {total === 1 ? "store" : "stores"}
                      </strong>
                    </>
                  );
                })()
              )
            ) : isLoading || !hasFetched || displayedDataUrl !== fetchUrl ? (
              <>
                {"Loading all stores with unpaid balance..."}
              </>
            ) : (
              <>
                {(() => {
                  const unpaidFiltered =
                    responseMeta?.filtered_total ??
                    responseMeta?.pagination?.filtered_total ??
                    responseMeta?.total_stores ??
                    responseMeta?.pagination?.total_stores ??
                    stores?.length ??
                    0;
                  const totalAll =
                    responseMeta?.total_stores ??
                    responseMeta?.pagination?.total_stores ??
                    unpaidFiltered ??
                    0;
                  return (
                    <>
                      Showing{" "}
                      <strong>
                        {unpaidFiltered} of {totalAll}{" "}
                        {totalAll === 1 ? "store" : "stores"}
                      </strong>{" "}
                      with unpaid balance
                    </>
                  );
                })()}
                {(appliedDateFrom || appliedDateTo || appliedMinBalance) && (
                  <>
                    {" ("}
                    {[
                      (appliedDateFrom || appliedDateTo)
                        ? `balance as of ${appliedDateTo ? formatDisplayDate(appliedDateTo) : "today"}${appliedDateFrom ? `, from ${formatDisplayDate(appliedDateFrom)}` : ""}`
                        : null,
                      appliedMinBalance
                        ? `at least $${parseFloat(appliedMinBalance).toFixed(2)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    {")"}
                  </>
                )}
              </>
            )}
          </span>
          <span className="selected-badge" id="selectedBadge" hidden={selectedCount === 0}>
            <span id="selectedText">{selectedCount} selected</span>
            <button
              type="button"
              className="link"
              id="clearSelection"
              onClick={resetSelection}
            >
              Clear selection
            </button>
          </span>
        </div>

        {/* Error notifications */}
        {hasFetched && errorContent && (
          <div className="empty">{errorContent}</div>
        )}
        {toggleError && (
          <div className="empty">{toggleError}</div>
        )}

        {/* ---------- Mobile Toolbar (select-all + sort-by-balance, hidden on desktop) ---------- */}
        <div className="mobile-toolbar">
          <label className="mobile-toolbar__select-all" htmlFor="mobileSelectAll">
            <input
              type="checkbox"
              id="mobileSelectAll"
              aria-label="Select all shown stores"
              checked={isAllSelected}
              ref={(el) => {
                if (el) el.indeterminate = isSomeSelected;
              }}
              onChange={handleSelectAllToggle}
              disabled={!stores || stores.length === 0}
            />
            <span>Select all</span>
          </label>
          <button
            type="button"
            className={`mobile-toolbar__sort-btn${sortOrder ? " mobile-toolbar__sort-btn--active" : ""}`}
            onClick={handleBalanceSortToggle}
            aria-label="Sort by balance"
          >
            <span>Balance</span>
            <span className="mobile-toolbar__sort-icon">
              {sortOrder === "asc" ? (
                <ArrowDropUpIcon sx={{ fontSize: 16 }} />
              ) : sortOrder === "desc" ? (
                <ArrowDropDownIcon sx={{ fontSize: 16 }} />
              ) : (
                <UnfoldMoreIcon sx={{ fontSize: 14 }} />
              )}
            </span>
          </button>
          <button
            type="button"
            className={`mobile-toolbar__sort-btn${payoutSortOrder ? " mobile-toolbar__sort-btn--active" : ""}`}
            onClick={handlePayoutSortToggle}
            aria-label="Sort by last payout date"
          >
            <span>Last payout</span>
            <span className="mobile-toolbar__sort-icon">
              {payoutSortOrder === "asc" ? (
                <ArrowDropUpIcon sx={{ fontSize: 16 }} />
              ) : payoutSortOrder === "desc" ? (
                <ArrowDropDownIcon sx={{ fontSize: 16 }} />
              ) : (
                <UnfoldMoreIcon sx={{ fontSize: 14 }} />
              )}
            </span>
          </button>
        </div>

        {/* ---------- Table Card ---------- */}
        <div className="card table-wrap">
          <table className="stores">
            <thead>
              <tr>
                <th rowSpan={2} className="th-checkbox">
                  {appliedUnpaidOnly && (
                    <input
                      type="checkbox"
                      id="selectAll"
                      aria-label="Select all shown stores"
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isSomeSelected;
                      }}
                      onChange={handleSelectAllToggle}
                      disabled={!stores || stores.length === 0}
                    />
                  )}
                </th>
                <th rowSpan={2}>Store</th>
                <th rowSpan={2}>Owner</th>
                <th rowSpan={2}>Studio org</th>
                <th rowSpan={2} className="num">Rate</th>
                <th className="group" id="commissionGroup" colSpan={rangeOn ? 5 : 4}>Commission</th>
                <th rowSpan={2} className={`stores-th-sortable${payoutSortOrder ? " stores-th-sortable--active" : ""}`} onClick={handlePayoutSortToggle} title="Sort by last payout date">
                  <span className="stores-balance-header-content" style={{ justifyContent: "flex-start" }}>
                    Last payout
                    <span className={`stores-balance-sort-icon ${payoutSortOrder ? "stores-balance-sort-icon--active" : ""}`}>
                      {payoutSortOrder === "asc" ? (
                        <ArrowDropUpIcon sx={{ fontSize: 18 }} />
                      ) : payoutSortOrder === "desc" ? (
                        <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 16 }} />
                      )}
                    </span>
                  </span>
                </th>
                {unpaidOnly && (
                  <th rowSpan={2}>Last transaction</th>
                )}
                <th rowSpan={2} className="th-actions"></th>
              </tr>
              <tr>
                {rangeOn && (
                  <th className="num in-range" id="rangeHead">Earned in range</th>
                )}
                <th className="num">Earned</th>
                <th className="num">Paid</th>
                <th className="num">Refunded</th>
                <th
                  className="num stores-th-sortable"
                  onClick={handleBalanceSortToggle}
                  title={rangeOn && appliedDateTo ? `Balance as of ${formatDisplayDate(appliedDateTo)}` : "Outstanding Balance"}
                >
                  <span className="stores-balance-header-content">
                    Balance
                    <span className={`stores-balance-sort-icon ${sortOrder ? "stores-balance-sort-icon--active" : ""}`}>
                      {sortOrder === "asc" ? (
                        <ArrowDropUpIcon sx={{ fontSize: 18 }} />
                      ) : sortOrder === "desc" ? (
                        <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 16 }} />
                      )}
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody
              id="storesBody">
              {isLoading && (!stores || stores.length === 0) ? (
                <tr>
                  <td colSpan={7 + (rangeOn ? 5 : 4) + (unpaidOnly ? 1 : 0)} className="empty">
                    Loading stores...
                  </td>
                </tr>
              ) : hasFetched && !errorContent && stores.length === 0 ? (
                <tr>
                  <td colSpan={7 + (rangeOn ? 5 : 4) + (unpaidOnly ? 1 : 0)} className="empty">
                    No stores match these filters.
                  </td>
                </tr>
              ) : (
                stores?.map((store: any, index: number) => {
                  const hasBalance = hasStoreBalance(store);
                  const isSelected = hasBalance && isStoreSelected(store.id);
                  const ownerName = `${store.store_owner?.first_name ?? ""} ${store.store_owner?.last_name ?? ""}`.trim();
                  const pct = store.commission_rate != null ? `${parseFloat(store.commission_rate)}%` : "15%";
                  const storeId = String(store.id);
                  const storeUrl = `${storeUrlBase}/${storeId}`;

                  const earned = store?.commission_earned != null ? parseFloat(store.commission_earned) : 0;
                  const earnedInRange = store?.commission_earned_in_range != null ? parseFloat(store.commission_earned_in_range) : 0;
                  const paid = store?.commission_paid != null ? parseFloat(store.commission_paid) : 0;
                  const refunded = store?.commission_refunded != null ? parseFloat(store.commission_refunded) : 0;
                  const balance = store?.commission_outstanding != null ? parseFloat(store.commission_outstanding) : 0;

                  // Payout date
                  const rawPayout = store.last_payout_date || store.last_payout || store.latest_payout_date;
                  const payoutDate = rawPayout ? formatDisplayDate(rawPayout) : "";

                  const rawTx = store.last_transaction_date || store.last_transaction || store.latest_transaction_date;
                  const txDate = rawTx ? formatDisplayDate(rawTx) : "";

                  return (
                    <tr
                      key={store.id}
                      data-index={index}
                      className={`${isSelected ? "is-selected" : ""}${store.status === 0 ? " stores-row--inactive" : ""}${!hasBalance ? " stores-row--unselectable" : ""}`}
                      onClick={(e) => handleRowClick(e, store.id, index)}
                      onPointerEnter={() => handleRowPointerEnter(index)}
                    >
                      {/* Desktop Checkbox Cell */}
                      <td
                        className="td-checkbox"
                        onPointerDown={hasBalance ? (e) => handleCheckboxPointerDown(e, store.id, index) : undefined}
                      >
                        {hasBalance && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            aria-label={`Select store #${store.id}`}
                            onChange={() => {}}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStoreSelect(e, store.id, index);
                            }}
                          />
                        )}
                      </td>

                      {/* Store Cell */}
                      <td>
                        <div className="store">
                          <StoreLogoUpload
                            storeId={store.id}
                            currentLogo={store.logo}
                            storeName={store.name}
                            fallbackClassName="store__logo"
                            imgClassName="store__logo-img"
                            onLogoChange={(newLogo) => updateStoreInStorage(store.id, { logo: newLogo })}
                          />
                          <div className="store-info-wrap">
                            <a
                              href={storeUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="store-title-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {toTitleCase(store.name)}
                            </a>
                            <span className="sub">
                              #{store.id} · Created {safeFormatDate(store.createdAt)}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Owner Cell */}
                      <td>
                        <span className="store-owner-name">{toTitleCase(ownerName) || ""}</span>
                        <span className="sub">{store.store_owner?.email || ""}</span>
                      </td>

                      {/* Studio Org Cell */}
                      <td className="td-studio-org">
                        {toTitleCase(store.studio_organization_name) || ""}
                      </td>

                      {/* Rate Cell */}
                      <td className="num">{pct}</td>

                      {/* Earned in range Cell (if active) */}
                      {rangeOn && (
                        <td className="num in-range">
                          {earnedInRange > 0.001 ? (
                            <Link
                              to={`/payouts/${store.id}`}
                              state={{ storeName: store.name }}
                              className="stores-financials-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {blankIfZero(earnedInRange)}
                            </Link>
                          ) : null}
                        </td>
                      )}

                      {/* Commission Earned Cell */}
                      <td className="num">
                        {earned > 0.001 ? (
                          <Link
                            to={`/payouts/${store.id}`}
                            state={{ storeName: store.name }}
                            className="stores-financials-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {blankIfZero(earned)}
                          </Link>
                        ) : null}
                      </td>

                      {/* Commission Paid Cell */}
                      <td className="num">
                        {paid > 0.001 ? (
                          <Link
                            to={`/payouts/${store.id}`}
                            state={{ storeName: store.name }}
                            className="stores-financials-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {blankIfZero(paid)}
                          </Link>
                        ) : null}
                      </td>

                      {/* Commission Refunded Cell */}
                      <td className="num">
                        {refunded > 0.001 ? (
                          <Link
                            to={`/payouts/${store.id}`}
                            state={{ storeName: store.name }}
                            className="stores-financials-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {blankIfZero(refunded)}
                          </Link>
                        ) : null}
                      </td>

                      {/* Commission Balance Cell */}
                      <td className="num">
                        {store.payment_state === "FAILED" ? (
                          <Link
                            to={`/payouts/${store.id}`}
                            state={{ storeName: store.name }}
                            className="stores-reconciliation-issue"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Payment reconciliation issue
                          </Link>
                        ) : balance > 0.001 ? (
                          <Link
                            to={`/payouts/${store.id}`}
                            state={{ storeName: store.name }}
                            className="stores-financials-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <strong>{money(balance)}</strong>
                          </Link>
                        ) : null}
                      </td>

                      {/* Last Payout Cell */}
                      <td>{payoutDate}</td>

                      {/* Last Transaction Cell (shown only with unpaid-balance filter) */}
                      {unpaidOnly && <td>{txDate}</td>}

                      {/* Actions Kebab Cell */}
                      <td className="td-actions">
                        <div className="menu-wrap">
                          <button
                            type="button"
                            className="kebab"
                            aria-label={`Actions for store #${store.id}`}
                            aria-haspopup="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuAnchor({ el: e.currentTarget, storeId: store.id });
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <circle cx="12" cy="5" r="1.8" />
                              <circle cx="12" cy="12" r="1.8" />
                              <circle cx="12" cy="19" r="1.8" />
                            </svg>
                          </button>
                        </div>
                      </td>

                      {/* Responsive Mobile Card View (renders exclusively on mobile <=767px) */}
                      <td className="mobile-card-td">
                        <div className="store-mobile-card">
                          <div className="mobile-card-header">
                            <div className="mobile-card-header-left">
                              {hasBalance && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  aria-label={`Select store #${store.id}`}
                                  onChange={() => {}}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStoreSelect(e, store.id, index);
                                  }}
                                />
                              )}
                              <StoreLogoUpload
                                storeId={store.id}
                                currentLogo={store.logo}
                                storeName={store.name}
                                fallbackClassName="store__logo"
                                imgClassName="store__logo-img"
                                onLogoChange={(newLogo) => updateStoreInStorage(store.id, { logo: newLogo })}
                              />
                              <div className="store-info-wrap">
                                <a
                                  href={storeUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mobile-card-store-name"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {toTitleCase(store.name)}
                                </a>
                                <span className="sub">
                                  #{store.id} · Created {safeFormatDate(store.createdAt)}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="kebab"
                              aria-label={`Actions for store #${store.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuAnchor({ el: e.currentTarget, storeId: store.id });
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <circle cx="12" cy="5" r="1.8" />
                                <circle cx="12" cy="12" r="1.8" />
                                <circle cx="12" cy="19" r="1.8" />
                              </svg>
                            </button>
                          </div>

                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Owner</span>
                            <span className="mobile-card-value">
                              {toTitleCase(ownerName) || "—"} ({store.store_owner?.email || "—"})
                            </span>
                          </div>

                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Studio Org</span>
                            <span className="mobile-card-value">{toTitleCase(store.studio_organization_name) || "—"}</span>
                          </div>

                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Rate</span>
                            <span className="mobile-card-value">{pct}</span>
                          </div>

                          {/* Commission Grid on Mobile */}
                          <div className="mobile-fin-grid">
                            {rangeOn && (
                              <div className="mobile-fin-item mobile-fin-inrange">
                                <span className="mobile-fin-title">Earned in range</span>
                                <span className="mobile-fin-num">
                                  {earnedInRange > 0.001 ? (
                                    <Link
                                      to={`/payouts/${store.id}`}
                                      state={{ storeName: store.name }}
                                      className="stores-financials-link"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {blankIfZero(earnedInRange)}
                                    </Link>
                                  ) : ""}
                                </span>
                              </div>
                            )}
                            <div className="mobile-fin-item">
                              <span className="mobile-fin-title">Earned</span>
                              <span className="mobile-fin-num">
                                {earned > 0.001 ? (
                                  <Link
                                    to={`/payouts/${store.id}`}
                                    state={{ storeName: store.name }}
                                    className="stores-financials-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {blankIfZero(earned)}
                                  </Link>
                                ) : ""}
                              </span>
                            </div>
                            <div className="mobile-fin-item">
                              <span className="mobile-fin-title">Paid</span>
                              <span className="mobile-fin-num">
                                {paid > 0.001 ? (
                                  <Link
                                    to={`/payouts/${store.id}`}
                                    state={{ storeName: store.name }}
                                    className="stores-financials-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {blankIfZero(paid)}
                                  </Link>
                                ) : ""}
                              </span>
                            </div>
                            <div className="mobile-fin-item">
                              <span className="mobile-fin-title">Refunded</span>
                              <span className="mobile-fin-num">
                                {refunded > 0.001 ? (
                                  <Link
                                    to={`/payouts/${store.id}`}
                                    state={{ storeName: store.name }}
                                    className="stores-financials-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {blankIfZero(refunded)}
                                  </Link>
                                ) : ""}
                              </span>
                            </div>
                            <div className="mobile-fin-item">
                              <span className="mobile-fin-title">Balance</span>
                              <span className="mobile-fin-balance">
                                {store.payment_state === "FAILED" ? (
                                  <Link
                                    to={`/payouts/${store.id}`}
                                    state={{ storeName: store.name }}
                                    className="stores-reconciliation-issue"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Payment reconciliation issue
                                  </Link>
                                ) : balance > 0.001 ? (
                                  <Link
                                    to={`/payouts/${store.id}`}
                                    state={{ storeName: store.name }}
                                    className="stores-financials-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <strong>{money(balance)}</strong>
                                  </Link>
                                ) : ""}
                              </span>
                            </div>
                          </div>

                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Last Payout</span>
                            <span className="mobile-card-value">{payoutDate}</span>
                          </div>

                          {unpaidOnly && (
                            <div className="mobile-card-row">
                              <span className="mobile-card-label">Last Transaction</span>
                              <span className="mobile-card-value">{txDate}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Global Action Menu */}
        <Menu
          anchorEl={menuAnchor?.el}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          onClick={(e) => e.stopPropagation()}
          classes={{ paper: "stores-action-menu-paper" }}
          disableScrollLock
        >
          {menuAnchor && (
            <>
              <MenuItem
                component={Link}
                to={`/orders/${menuAnchor.storeId}`}
                state={{
                  storeName: stores.find((s: any) => s.id === menuAnchor.storeId)?.name,
                  storeId: menuAnchor.storeId,
                }}
                onClick={() => setMenuAnchor(null)}
              >
                Orders
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const s = stores.find((st: any) => st.id === menuAnchor.storeId);
                  setCommissionDialog({
                    open: true,
                    storeId: menuAnchor.storeId,
                    currentRate: s?.commission_rate ?? null,
                  });
                  setMenuAnchor(null);
                }}
              >
                Commission
              </MenuItem>
              <MenuItem
                component={Link}
                to={`/payouts/${menuAnchor.storeId}`}
                state={{ storeName: stores.find((s: any) => s.id === menuAnchor.storeId)?.name }}
                onClick={() => setMenuAnchor(null)}
              >
                Payouts
              </MenuItem>
              <MenuItem
                component={Link}
                to={`/product-groups/${menuAnchor.storeId}`}
                state={{ storeName: stores.find((s: any) => s.id === menuAnchor.storeId)?.name }}
                onClick={() => setMenuAnchor(null)}
              >
                Product Groups
              </MenuItem>

              <hr className="stores-menu-divider" />

              <MenuItem
                component="a"
                href={`${storeUrlBase}/${menuAnchor.storeId}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenuAnchor(null)}
              >
                View store
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const s = stores.find((st: any) => st.id === menuAnchor.storeId);
                  setShareDialog({
                    open: true,
                    storeId: menuAnchor.storeId,
                    storeName: s?.name || "",
                  });
                  setMenuAnchor(null);
                }}
              >
                Share
              </MenuItem>

              <hr className="stores-menu-divider" />

              <MenuItem
                component={Link}
                to={`/edit-store/${menuAnchor.storeId}`}
                state={{
                  storeName: stores.find((s: any) => s.id === menuAnchor.storeId)?.name,
                  studio_org_name: stores.find((s: any) => s.id === menuAnchor.storeId)?.studio_organization_name,
                  store_description: stores.find((s: any) => s.id === menuAnchor.storeId)?.description,
                }}
                onClick={() => setMenuAnchor(null)}
              >
                Edit Store
              </MenuItem>
              <MenuItem
                component={Link}
                to={`/edit-store-owner/${menuAnchor.storeId}`}
                state={{
                  owner_first_name: stores.find((s: any) => s.id === menuAnchor.storeId)?.store_owner?.first_name,
                  owner_last_name: stores.find((s: any) => s.id === menuAnchor.storeId)?.store_owner?.last_name,
                  owner_email: stores.find((s: any) => s.id === menuAnchor.storeId)?.store_owner?.email,
                  owner_address: stores.find((s: any) => s.id === menuAnchor.storeId)?.store_owner?.address,
                }}
                onClick={() => setMenuAnchor(null)}
              >
                Edit Profile
              </MenuItem>

              <hr className="stores-menu-divider" />

              {(() => {
                const s = stores.find((st: any) => st.id === menuAnchor.storeId);
                const isActive = s?.status === 1;
                return (
                  <MenuItem
                    className={isActive ? "MuiMenuItem-root--danger danger" : "MuiMenuItem-root--activate"}
                    onClick={() => {
                      const btn = document.getElementById(`toggle-btn-${menuAnchor.storeId}`);
                      btn?.click();
                    }}
                  >
                    {isActive ? "Deactivate" : "Activate"}
                    <span className="stores-hidden-toggle-wrap">
                      <StoreToggleBtn
                        storeId={menuAnchor.storeId}
                        isActive={isActive}
                        onDone={() => {
                          setMenuAnchor(null);
                          updateStoreInStorage(menuAnchor.storeId, { status: isActive ? 0 : 1 });
                        }}
                        onError={(content) => {
                          setMenuAnchor(null);
                          setToggleError(content);
                        }}
                      />
                    </span>
                  </MenuItem>
                );
              })()}
            </>
          )}
        </Menu>

        {/* Infinite scroll triggers & Skeletons */}
        {hasFetched && hasMore && (
          <div ref={observerTargetRef} className="stores-infinite-scroll-trigger" />
        )}

        {isLoadingMore && (
          <div className="stores-skeleton-wrapper">
            {[1, 2, 3].map((i) => (
              <div key={i} className="stores-skeleton-row">
                <div className="stores-skeleton-box" />
                <div className="stores-skeleton-circle" />
                <div className="stores-skeleton-line" />
              </div>
            ))}
          </div>
        )}

        {hasMore && !isLoadingMore && (
          <div className="stores-load-more-btn-wrapper">
            <button
              type="button"
              className="stores-load-more-btn"
              onClick={fetchMoreStores}
            >
              Load More
            </button>
          </div>
        )}

        {/* Dialogs */}
        <StoreCommissionDialog
          open={commissionDialog.open}
          storeId={commissionDialog.storeId}
          currentRate={commissionDialog.currentRate}
          onClose={() => setCommissionDialog((d) => ({ ...d, open: false }))}
          onSave={(newRate) => updateStoreInStorage(commissionDialog.storeId, { commission_rate: newRate })}
        />

        <ShareStoreDialog
          open={shareDialog.open}
          onClose={() => setShareDialog((d) => ({ ...d, open: false }))}
          storeUrl={`${storeUrlBase}/${shareDialog.storeId}`}
          storeName={shareDialog.storeName}
        />
        </div>
      </div>
    </div>
  );
};

export default Stores;